import type { DeviceSync, NowPlayingPayload } from "@/player/lib/offline-types";
import {
  getBoundaries,
  getTimedEvents,
  mediaAssetById,
} from "@/player/lib/scheduling";
import { playlistById } from "@workspace/player-core";
import { TELEMETRY_QUEUE_MAX_EVENTS } from "@workspace/player-shell";
import { mediaCacheUrlKey } from "@/player/lib/media-url-key";
import { MEDIA_CACHE } from "@/player/lib/media-cache-name";
import { shouldCacheMediaResponse } from "@/player/lib/sw-policy";
import {
  planCacheCapEviction,
  reconcileCacheIndex,
  type StoredCacheIndex,
} from "@/player/lib/media-cache-cap";
import {
  type OfflineReadinessReport,
  collectSyncMedia,
  computeOfflineReadiness,
  rekeySyncMedia,
  resolveOfflineCacheMaxBytes,
} from "@workspace/player-shell/offline-window";

/**
 * Offline persistence for the player. Three concerns live here:
 *
 *  1. The last resolved sync payload (schedule + playlists) is mirrored to
 *     IndexedDB so the player can keep running its program across reloads and
 *     network outages.
 *  2. Referenced media (audio/video/artwork) is proactively pulled into a
 *     Cache Storage bucket while online so the audio element can be served from
 *     cache by the service worker once the network drops.
 *  3. now-playing "playing" events that occur while disconnected are queued and
 *     replayed on reconnect so usage logging survives offline stretches.
 *
 * Everything is best-effort: a missing IndexedDB/Cache API (private mode,
 * unsupported browser) must never break playback.
 */

const DB_NAME = "omnimedia-player";
const DB_VERSION = 1;
const KV_STORE = "kv";
const EVENTS_STORE = "events";
const LAST_SYNC_KEY = "last-sync";
const SYNC_VERSION_KEY = "sync-version";
const MEDIA_INDEX_KEY = "media-cache-index";

export { MEDIA_CACHE };

let dbPromise: Promise<IDBDatabase | null> | null = null;

function openDb(): Promise<IDBDatabase | null> {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve) => {
    try {
      if (typeof indexedDB === "undefined") {
        resolve(null);
        return;
      }
      const req = indexedDB.open(DB_NAME, DB_VERSION);
      req.onupgradeneeded = () => {
        const db = req.result;
        if (!db.objectStoreNames.contains(KV_STORE)) {
          db.createObjectStore(KV_STORE);
        }
        if (!db.objectStoreNames.contains(EVENTS_STORE)) {
          db.createObjectStore(EVENTS_STORE, {
            keyPath: "id",
            autoIncrement: true,
          });
        }
      };
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => resolve(null);
    } catch {
      resolve(null);
    }
  });
  return dbPromise;
}

function tx(
  db: IDBDatabase,
  store: string,
  mode: IDBTransactionMode,
): IDBObjectStore {
  return db.transaction(store, mode).objectStore(store);
}

// ---- last sync payload ----

export async function saveLastSync(sync: DeviceSync): Promise<void> {
  const db = await openDb();
  if (!db) return;
  await new Promise<void>((resolve) => {
    try {
      const req = tx(db, KV_STORE, "readwrite").put(sync, LAST_SYNC_KEY);
      req.onsuccess = req.onerror = () => resolve();
    } catch {
      resolve();
    }
  });
}

export async function saveSyncVersion(version: string): Promise<void> {
  const db = await openDb();
  if (!db) return;
  await new Promise<void>((resolve) => {
    try {
      const req = tx(db, KV_STORE, "readwrite").put(version, SYNC_VERSION_KEY);
      req.onsuccess = req.onerror = () => resolve();
    } catch {
      resolve();
    }
  });
}

export async function loadSyncVersion(): Promise<string | null> {
  const db = await openDb();
  if (!db) return null;
  return new Promise<string | null>((resolve) => {
    try {
      const req = tx(db, KV_STORE, "readonly").get(SYNC_VERSION_KEY);
      req.onsuccess = () => resolve((req.result as string) ?? null);
      req.onerror = () => resolve(null);
    } catch {
      resolve(null);
    }
  });
}

export async function loadLastSync(): Promise<DeviceSync | null> {
  const db = await openDb();
  if (!db) return null;
  return new Promise<DeviceSync | null>((resolve) => {
    try {
      const req = tx(db, KV_STORE, "readonly").get(LAST_SYNC_KEY);
      req.onsuccess = () => resolve((req.result as DeviceSync) ?? null);
      req.onerror = () => resolve(null);
    } catch {
      resolve(null);
    }
  });
}

// ---- offline now-playing queue ----

export type TelemetryKind = "now-playing" | "play-log" | "status" | "heartbeat";

export interface QueuedEvent {
  id?: number;
  kind: TelemetryKind;
  payload: NowPlayingPayload | Record<string, unknown>;
  at: number;
  clientEventId?: string;
}

export async function enqueueEvent(
  payload: NowPlayingPayload,
  kind: TelemetryKind = "now-playing",
): Promise<void> {
  const db = await openDb();
  if (!db) return;
  const at = Date.now();
  const clientEventId =
    kind === "now-playing" && payload.itemId
      ? `np:${payload.itemId}:${at}`
      : undefined;
  await new Promise<void>((resolve) => {
    try {
      const store = tx(db, EVENTS_STORE, "readwrite");
      store.add({
        kind,
        payload,
        at,
        clientEventId,
      });
      // Cap the queue at the shared player-shell limit, dropping the OLDEST
      // events on overflow (mirrors the native AsyncStorage queue): unbounded,
      // a long-offline desktop accumulates a queue that outgrows both storage
      // and any batch flush. Same transaction as the add, so a failure can
      // never commit an over-cap queue.
      const countReq = store.count();
      countReq.onsuccess = () => {
        let excess = countReq.result - TELEMETRY_QUEUE_MAX_EVENTS;
        if (excess <= 0) return;
        // Keys auto-increment, so cursor order is insertion order.
        const cursorReq = store.openCursor();
        cursorReq.onsuccess = () => {
          const cursor = cursorReq.result;
          if (!cursor || excess <= 0) return;
          cursor.delete();
          excess -= 1;
          if (excess > 0) cursor.continue();
        };
      };
      const t = store.transaction;
      t.oncomplete = t.onerror = t.onabort = () => resolve();
    } catch {
      resolve();
    }
  });
}

/** Remove all queued events after a successful batch flush. */
export async function clearEvents(): Promise<void> {
  const db = await openDb();
  if (!db) return;
  await new Promise<void>((resolve) => {
    try {
      const req = tx(db, EVENTS_STORE, "readwrite").clear();
      req.onsuccess = req.onerror = () => resolve();
    } catch {
      resolve();
    }
  });
}

/** Read queued events oldest-first WITHOUT removing them. */
export async function peekEvents(): Promise<QueuedEvent[]> {
  const db = await openDb();
  if (!db) return [];
  return new Promise<QueuedEvent[]>((resolve) => {
    try {
      const getAll = tx(db, EVENTS_STORE, "readonly").getAll();
      getAll.onsuccess = () => {
        const events = (getAll.result as QueuedEvent[]) ?? [];
        resolve(events.sort((a, b) => a.at - b.at));
      };
      getAll.onerror = () => resolve([]);
    } catch {
      resolve([]);
    }
  });
}

/** Remove a single queued event by id once it has been successfully sent. */
export async function deleteEvent(id: number): Promise<void> {
  const db = await openDb();
  if (!db) return;
  await new Promise<void>((resolve) => {
    try {
      const req = tx(db, EVENTS_STORE, "readwrite").delete(id);
      req.onsuccess = req.onerror = () => resolve();
    } catch {
      resolve();
    }
  });
}

// ---- media pre-caching ----

function playlistItemUrls(
  urls: Set<string>,
  playlistId: string | null | undefined,
  sync: DeviceSync,
): void {
  const playlist = playlistById(sync, playlistId);
  if (!playlist) return;
  for (const item of playlist.items) {
    if (item.url) urls.add(item.url);
    if (item.artworkUrl) urls.add(item.artworkUrl);
  }
}

function mediaUrls(sync: DeviceSync): string[] {
  const urls = new Set<string>();
  for (const playlist of sync.playlists) {
    for (const item of playlist.items) {
      if (item.url) urls.add(item.url);
      if (item.artworkUrl) urls.add(item.artworkUrl);
    }
  }

  for (const event of getTimedEvents(sync)) {
    if (event.contentType === "playlist") {
      playlistItemUrls(urls, event.playlistId, sync);
    } else if (event.mediaAssetId) {
      const item = mediaAssetById(sync, event.mediaAssetId);
      if (item?.url) urls.add(item.url);
      if (item?.artworkUrl) urls.add(item.artworkUrl);
    }
  }

  for (const boundary of getBoundaries(sync)) {
    playlistItemUrls(urls, boundary.sourceId, sync);
  }

  return [...urls];
}

// ---- media byte index (feeds the offlineCacheMaxMb cap) ----

/** Cache-Storage URL key → observed size + first-cached time (IndexedDB KV). */
export async function loadMediaCacheIndex(): Promise<StoredCacheIndex> {
  const db = await openDb();
  if (!db) return {};
  return new Promise<StoredCacheIndex>((resolve) => {
    try {
      const req = tx(db, KV_STORE, "readonly").get(MEDIA_INDEX_KEY);
      req.onsuccess = () => resolve((req.result as StoredCacheIndex) ?? {});
      req.onerror = () => resolve({});
    } catch {
      resolve({});
    }
  });
}

async function saveMediaCacheIndex(index: StoredCacheIndex): Promise<void> {
  const db = await openDb();
  if (!db) return;
  await new Promise<void>((resolve) => {
    try {
      const req = tx(db, KV_STORE, "readwrite").put(index, MEDIA_INDEX_KEY);
      req.onsuccess = req.onerror = () => resolve();
    } catch {
      resolve();
    }
  });
}

/**
 * Per-origin fetch mode memo. CORS-mode responses expose their size
 * (Content-Length) so the cache cap can do exact accounting; origins without a
 * CORS rule throw on the first attempt and drop to opaque no-cors for the rest
 * of the session (sizes then come from the storage estimate). Adding the CORS
 * rule on the media buckets is what upgrades a device to exact accounting —
 * see docs/r2-setup.md.
 */
const fetchModeByOrigin = new Map<string, "cors" | "no-cors">();

async function fetchForCache(url: string): Promise<Response | null> {
  let origin: string;
  try {
    origin = new URL(url).origin;
  } catch {
    return null;
  }
  if ((fetchModeByOrigin.get(origin) ?? "cors") === "cors") {
    try {
      const res = await fetch(url, { mode: "cors", cache: "no-store" });
      fetchModeByOrigin.set(origin, "cors");
      return res;
    } catch {
      fetchModeByOrigin.set(origin, "no-cors");
    }
  }
  return fetch(url, { mode: "no-cors", cache: "no-store" });
}

/** Readable response size, or null for opaque responses (unknowable). */
function responseBytes(res: Response): number | null {
  if (res.type === "opaque") return null;
  const raw = res.headers.get("content-length");
  if (!raw) return null;
  const bytes = Number(raw);
  return Number.isFinite(bytes) && bytes > 0 ? bytes : null;
}

/**
 * Drop a single cached media entry by its stable (query-less) key. Called
 * from the playback error paths BEFORE requesting a resync: if the cached
 * body is what's failing (poisoned/corrupt entry), serving it cache-first
 * would otherwise defeat every retry no matter how fresh the new URL is.
 */
export async function evictMediaCacheEntry(url: string): Promise<void> {
  if (typeof caches === "undefined") return;
  try {
    const cache = await caches.open(MEDIA_CACHE);
    await cache.delete(mediaCacheUrlKey(url), {
      ignoreVary: true,
      ignoreSearch: true,
    });
  } catch {
    // Best-effort: eviction must never block the retry itself.
  }
}

/**
 * Warm the media cache for every item referenced by a sync payload. Tries CORS
 * first (readable sizes for the cache cap), falling back per-origin to no-cors
 * opaque responses the service worker replays to the audio/video element when
 * offline. Runs sequentially with a small concurrency cap to avoid saturating
 * the connection. Newly cached entries are recorded in the byte index that
 * `enforceCacheCap` consumes.
 */
export async function prefetchMedia(sync: DeviceSync): Promise<void> {
  if (typeof caches === "undefined") return;
  let cache: Cache;
  try {
    cache = await caches.open(MEDIA_CACHE);
  } catch {
    return;
  }

  const urls = mediaUrls(sync);
  const CONCURRENCY = 3;
  let cursor = 0;
  const indexUpdates: StoredCacheIndex = {};

  async function worker(): Promise<void> {
    while (cursor < urls.length) {
      const url = urls[cursor++];
      try {
        // Presigned URLs are cached under their query-less key so cached
        // media survives signature rotation (see media-url-key.ts); the
        // fetch itself still uses the full signed URL.
        const key = mediaCacheUrlKey(url);
        const existing = await cache.match(key, { ignoreVary: true });
        if (existing) continue;
        const res = await fetchForCache(url);
        // Opaque responses report status 0 and are cached regardless; readable
        // (CORS) error statuses — e.g. an expired presign's 403 — are not.
        // Never cache 206 partials (they poison later full reads).
        if (res && shouldCacheMediaResponse(res)) {
          await cache.put(key, res.clone());
          indexUpdates[key] = { bytes: responseBytes(res), cachedAt: Date.now() };
        }
      } catch {
        // Best-effort: a single failed prefetch must not abort the rest.
      }
    }
  }

  await Promise.all(
    Array.from({ length: Math.min(CONCURRENCY, urls.length) }, worker),
  );

  if (Object.keys(indexUpdates).length > 0) {
    try {
      const index = await loadMediaCacheIndex();
      await saveMediaCacheIndex({ ...index, ...indexUpdates });
    } catch {
      // Missing sizes degrade to estimates in enforceCacheCap.
    }
  }
}

/** Drop cached media no longer referenced by the current sync payload. */
export async function pruneMedia(sync: DeviceSync): Promise<void> {
  if (typeof caches === "undefined") return;
  try {
    const cache = await caches.open(MEDIA_CACHE);
    // Compare by stable key on both sides so rotated presigned URLs never
    // read as "unreferenced" (which would evict still-needed media).
    const keep = new Set(mediaUrls(sync).map(mediaCacheUrlKey));
    const keys = await cache.keys();
    await Promise.all(
      keys.map((req) =>
        keep.has(mediaCacheUrlKey(req.url)) ? undefined : cache.delete(req),
      ),
    );
  } catch {
    // ignore
  }
}

/**
 * Bring the media cache under the device's `offlineCacheMaxMb` (from
 * `device.settings` on the sync payload). Runs AFTER prefetch + prune — the
 * documented prefetch-before-prune/evict order (docs/player-offline.md) — so
 * a network cut mid-cycle never leaves the cache without still-needed media.
 * Victim planning is pure (`planCacheCapEviction`, backed by the shared
 * `planEviction` policy); this adapter observes Cache Storage + the byte
 * index + the storage estimate and applies the plan.
 */
export async function enforceCacheCap(sync: DeviceSync): Promise<void> {
  if (typeof caches === "undefined") return;
  try {
    const cache = await caches.open(MEDIA_CACHE);
    const requests = await cache.keys();
    if (requests.length === 0) return;
    const cachedUrlKeys = requests.map((req) => mediaCacheUrlKey(req.url));
    const storedIndex = await loadMediaCacheIndex();

    let storageUsageBytes: number | null = null;
    try {
      if (typeof navigator !== "undefined" && navigator.storage?.estimate) {
        storageUsageBytes = (await navigator.storage.estimate()).usage ?? null;
      }
    } catch {
      // Estimates are optional; the flat fallback applies.
    }

    const victims = new Set(
      planCacheCapEviction(sync, { cachedUrlKeys, storedIndex, storageUsageBytes }),
    );

    if (victims.size > 0) {
      await Promise.all(
        requests.map((req) =>
          victims.has(mediaCacheUrlKey(req.url)) ? cache.delete(req) : undefined,
        ),
      );
    }

    // Rewrite the index to cover exactly what survived (drops victims and
    // entries pruned earlier in the cycle).
    const remaining: StoredCacheIndex = {};
    for (const key of cachedUrlKeys) {
      if (victims.has(key)) continue;
      remaining[key] = storedIndex[key] ?? { bytes: null, cachedAt: 0 };
    }
    await saveMediaCacheIndex(remaining);
  } catch {
    // Best-effort: cap enforcement must never break playback.
  }
}

/**
 * Wipe all device-scoped offline state. Called when releasing a screen so a
 * subsequent claim never hydrates the previous device's schedule, queued
 * events, or cached media.
 */
export async function clearOfflineStore(): Promise<void> {
  const db = await openDb();
  if (db) {
    await new Promise<void>((resolve) => {
      try {
        const t = db.transaction([KV_STORE, EVENTS_STORE], "readwrite");
        t.objectStore(KV_STORE).clear();
        t.objectStore(EVENTS_STORE).clear();
        t.oncomplete = t.onerror = t.onabort = () => resolve();
      } catch {
        resolve();
      }
    });
  }
  if (typeof caches !== "undefined") {
    try {
      await caches.delete(MEDIA_CACHE);
    } catch {
      // ignore
    }
  }
}

// ---- offline readiness (CB-162) ----

/**
 * How much of the current window's media is actually cached locally.
 *
 * Observes the same three things `enforceCacheCap` does, in the same order, so
 * readiness and eviction can never disagree about what is on disk. In
 * particular it trusts Cache Storage over the byte index: an indexed key whose
 * response is gone counts as NOT cached, which the previous version got wrong.
 *
 * Web keys its cache by query-stripped URL rather than by content id, so the
 * targets are re-keyed first; the content id rides along in `id` so the report
 * never carries a presigned URL.
 */
export async function offlineReadiness(
  sync: DeviceSync,
): Promise<OfflineReadinessReport | null> {
  if (typeof caches === "undefined") return null;
  try {
    const cache = await caches.open(MEDIA_CACHE);
    const requests = await cache.keys();
    const cachedUrlKeys = requests.map((req) => mediaCacheUrlKey(req.url));
    const storedIndex = await loadMediaCacheIndex();

    let storageUsageBytes: number | null = null;
    try {
      if (typeof navigator !== "undefined" && navigator.storage?.estimate) {
        storageUsageBytes = (await navigator.storage.estimate()).usage ?? null;
      }
    } catch {
      // Estimates are optional; the flat fallback applies.
    }

    return computeOfflineReadiness({
      targets: rekeySyncMedia(collectSyncMedia(sync), (target) =>
        mediaCacheUrlKey(target.url),
      ),
      index: reconcileCacheIndex({
        cachedUrlKeys,
        storedIndex,
        storageUsageBytes,
      }),
      maxBytes: resolveOfflineCacheMaxBytes(sync.device?.settings),
      syncVersion: sync.syncVersion ?? null,
    });
  } catch {
    return null;
  }
}
